import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import api from '../services/api';
import { toast } from '../components/Toast';
import { confirm } from '../components/ConfirmDialog';
import DjamannLoader from '../components/DjamannLoader';
import logger from '../utils/logger';
import {
  Tag, Star, Gem, Flame, Heart, HeartHandshake, UserPlus, User, Building, Briefcase,
  Target, MapPin, Gift, Coins, ShoppingCart, Megaphone, Users, X, Plus, Pencil, Trash2,
  Filter, Download, Moon, CreditCard, ArrowLeft, Check, ChevronLeft, ChevronRight,
  ChevronsLeft, ChevronsRight, ArrowRight
} from 'lucide-react';

// Variables disponibles pour les messages
const AVAILABLE_VARIABLES = [
  { var: '{nom}', label: 'Nom complet', example: 'Amadou Diallo' },
  { var: '{prenom}', label: 'Prénom', example: 'Amadou' },
  { var: '{telephone}', label: 'Téléphone', example: '77 123 45 67' },
  { var: '{email}', label: 'Email', example: 'client@email.com' },
  { var: '{total_achats}', label: 'Total achats', example: '150 000 XOF' },
  { var: '{nb_achats}', label: 'Nombre d\'achats', example: '12' },
  { var: '{dernier_achat}', label: 'Dernier achat', example: '15/01/2026' },
  { var: '{solde_credit}', label: 'Solde crédit', example: '25 000 XOF' },
  { var: '{reste_a_payer}', label: 'Reste à payer', example: '75 000 XOF' },
  { var: '{segments}', label: 'Segments', example: 'VIP, Fidèle' },
];

// Couleurs pour les segments
const SEGMENT_COLORS = [
  { value: '#6366f1', label: 'Indigo' },
  { value: '#8b5cf6', label: 'Violet' },
  { value: '#ec4899', label: 'Rose' },
  { value: '#ef4444', label: 'Rouge' },
  { value: '#f97316', label: 'Orange' },
  { value: '#eab308', label: 'Jaune' },
  { value: '#22c55e', label: 'Vert' },
  { value: '#14b8a6', label: 'Teal' },
  { value: '#3b82f6', label: 'Bleu' },
  { value: '#6b7280', label: 'Gris' },
];

// Icônes pour les segments
// Identifiants string pour les icônes (sérialisables en JSON)
const SEGMENT_ICON_IDS = [
  'tag', 'star', 'gem', 'flame', 'heart', 'hearthandshake',
  'userplus', 'user', 'building', 'briefcase', 'target',
  'mappin', 'gift', 'coins', 'cart'
];

// Mapping identifiant -> composant React
const ICON_COMPONENTS = {
  tag: Tag,
  star: Star,
  gem: Gem,
  flame: Flame,
  heart: Heart,
  hearthandshake: HeartHandshake,
  userplus: UserPlus,
  user: User,
  building: Building,
  briefcase: Briefcase,
  target: Target,
  mappin: MapPin,
  gift: Gift,
  coins: Coins,
  cart: ShoppingCart,
};

// Fonction pour afficher une icône à partir de son identifiant
const getSegmentIcon = (iconId, size = 'w-4 h-4') => {
  const IconComp = ICON_COMPONENTS[iconId];
  if (IconComp) return <IconComp className={size} />;
  return <Tag className={size} />;
};

const BusinessMarketing = () => {
  // États
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState(null);
  const [segments, setSegments] = useState([]);
  const [clients, setClients] = useState([]);
  const [campaigns, setCampaigns] = useState([]);
  const [selectedClients, setSelectedClients] = useState([]);
  const [activeTab, setActiveTab] = useState('clients'); // 'clients', 'segments', 'campaigns'
  
  // Pagination
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 15;
  
  // Filtres
  const [filters, setFilters] = useState({
    segment_ids: [],
    min_achats: '',
    max_achats: '',
    inactif_depuis_jours: '',
    actif_depuis_jours: '',
    avec_credit: false,
    min_nb_achats: '',
    nouveaux_jours: '',
    search: ''
  });
  const [showFilters, setShowFilters] = useState(false);

  // Modal segment
  const [showSegmentModal, setShowSegmentModal] = useState(false);
  const [editingSegment, setEditingSegment] = useState(null);
  const [segmentForm, setSegmentForm] = useState({ nom: '', description: '', couleur: '#6366f1', icone: 'tag' });

  // Modal message / export
  const [showMessageModal, setShowMessageModal] = useState(false);
  const [messageTemplate, setMessageTemplate] = useState('Bonjour {prenom},\n\nNous avons une offre spéciale pour vous !\n\nCordialement,\nVotre équipe');
  const [campaignName, setCampaignName] = useState('');
  const [campaignType, setCampaignType] = useState('personnalise');
  const [saveCampaign, setSaveCampaign] = useState(true);
  const [previewMessage, setPreviewMessage] = useState('');
  const [exporting, setExporting] = useState(false);

  // Chargement initial
  useEffect(() => {
    loadData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loadData = async () => {
    setLoading(true);
    try {
      const [statsRes, segmentsRes, campaignsRes] = await Promise.all([
        api.get('/marketing/stats'),
        api.get('/marketing/segments'),
        api.get('/marketing/campaigns')
      ]);
      
      if (statsRes.data.success) setStats(statsRes.data.data);
      if (segmentsRes.data.success) setSegments(segmentsRes.data.data);
      if (campaignsRes.data.success) setCampaigns(campaignsRes.data.data);
      
      // Charger les clients
      await loadClients();
    } catch (err) {
      logger.error(err);
    } finally {
      setLoading(false);
    }
  };

  const loadClients = useCallback(async () => {
    try {
      const params = new URLSearchParams();
      if (filters.segment_ids.length > 0) params.append('segment_ids', filters.segment_ids.join(','));
      if (filters.min_achats) params.append('min_achats', filters.min_achats);
      if (filters.max_achats) params.append('max_achats', filters.max_achats);
      if (filters.inactif_depuis_jours) params.append('inactif_depuis_jours', filters.inactif_depuis_jours);
      if (filters.actif_depuis_jours) params.append('actif_depuis_jours', filters.actif_depuis_jours);
      if (filters.avec_credit) params.append('avec_credit', 'true');
      if (filters.min_nb_achats) params.append('min_nb_achats', filters.min_nb_achats);
      if (filters.nouveaux_jours) params.append('nouveaux_jours', filters.nouveaux_jours);
      if (filters.search) params.append('search', filters.search);

      const res = await api.get(`/marketing/clients?${params.toString()}`);
      if (res.data.success) {
        setClients(res.data.data);
        setCurrentPage(1); // Reset pagination on filter change
      }
    } catch (err) {
      logger.error(err);
    }
  }, [filters]);

  useEffect(() => {
    const timer = setTimeout(() => {
      loadClients();
    }, 300);
    return () => clearTimeout(timer);
  }, [loadClients]);

  // Gestion des segments
  const openSegmentModal = (segment = null) => {
    if (segment) {
      setEditingSegment(segment);
      setSegmentForm({
        nom: segment.nom,
        description: segment.description || '',
        couleur: segment.couleur || '#6366f1',
        icone: segment.icone || 'tag'
      });
    } else {
      setEditingSegment(null);
      setSegmentForm({ nom: '', description: '', couleur: '#6366f1', icone: 'tag' });
    }
    setShowSegmentModal(true);
  };

  const handleSegmentSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingSegment) {
        await api.put(`/marketing/segments/${editingSegment.id}`, segmentForm);
      } else {
        await api.post('/marketing/segments', segmentForm);
      }
      setShowSegmentModal(false);
      loadData();
    } catch (err) {
      logger.error(err);
      toast.error('Erreur lors de l\'enregistrement');
    }
  };

  const deleteSegment = async (id) => {
    if (!await confirm({ message: 'Supprimer ce segment ?', type: 'danger', confirmText: 'Supprimer' })) return;
    try {
      await api.delete(`/marketing/segments/${id}`);
      loadData();
    } catch (err) {
      logger.error(err);
    }
  };

  const addToSegment = async (segmentId) => {
    if (selectedClients.length === 0) {
      toast.warning('Sélectionnez au moins un client');
      return;
    }
    try {
      await api.post(`/marketing/segments/${segmentId}/clients`, { client_ids: selectedClients });
      loadClients();
      setSelectedClients([]);
      toast.success('Clients ajoutés au segment');
    } catch (err) {
      logger.error(err);
    }
  };

  // Sélection clients
  const toggleSelectAll = () => {
    if (selectedClients.length === clients.length) {
      setSelectedClients([]);
    } else {
      setSelectedClients(clients.map(c => c.id));
    }
  };

  const toggleSelectClient = (id) => {
    setSelectedClients(prev => 
      prev.includes(id) ? prev.filter(i => i !== id) : [...prev, id]
    );
  };

  // Preview message
  const handlePreview = async (clientId) => {
    try {
      const res = await api.post('/marketing/preview', {
        message_template: messageTemplate,
        client_id: clientId
      });
      if (res.data.success) {
        setPreviewMessage(res.data.preview);
      }
    } catch (err) {
      logger.error(err);
    }
  };

  // Export Excel
  const handleExport = async () => {
    if (selectedClients.length === 0) {
      toast.warning('Sélectionnez au moins un client');
      return;
    }

    setExporting(true);
    try {
      const res = await api.post('/marketing/export', {
        client_ids: selectedClients,
        message_template: messageTemplate,
        save_campaign: saveCampaign,
        campaign_name: campaignName || `Campagne du ${new Date().toLocaleDateString('fr-FR')}`,
        campaign_type: campaignType,
        filtres: filters
      });

      if (res.data.success) {
        // Générer le fichier Excel côté client
        const exportData = res.data.data;
        generateExcel(exportData);
        
        setShowMessageModal(false);
        setSelectedClients([]);
        if (saveCampaign) {
          loadData(); // Recharger pour voir la nouvelle campagne
        }
      }
    } catch (err) {
      logger.error(err);
      toast.error('Erreur lors de l\'export');
    } finally {
      setExporting(false);
    }
  };

  // Génération Excel (CSV compatible Excel)
  const generateExcel = (data) => {
    const headers = ['Nom', 'Téléphone', 'Email', 'Adresse', 'Total Achats', 'Nb Achats', 'Dernier Achat', 'Reste à payer', 'Reste factures', 'Reste prestations', 'Segments', 'Message Personnalisé'];
    
    const rows = data.map(client => [
      client.nom || '',
      client.telephone || '',
      client.email || '',
      client.adresse || '',
      client.total_achats || 0,
      client.nb_achats || 0,
      client.dernier_achat || '',
      client.reste_total || 0,
      client.reste_factures || 0,
      client.reste_prestations || 0,
      client.segments_noms || '',
      (client.message_personnalise || '').replace(/\n/g, ' ')
    ]);

    // BOM pour UTF-8
    const BOM = '\uFEFF';
    const csvContent = BOM + [
      headers.join(';'),
      ...rows.map(row => row.map(cell => `"${String(cell).replace(/"/g, '""')}"`).join(';'))
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = `marketing_${new Date().toISOString().split('T')[0]}.csv`;
    link.click();
  };

  // Insérer variable dans le message
  const insertVariable = (variable) => {
    setMessageTemplate(prev => prev + variable);
  };

  // Reset filtres
  const resetFilters = () => {
    setFilters({
      segment_ids: [],
      min_achats: '',
      max_achats: '',
      inactif_depuis_jours: '',
      actif_depuis_jours: '',
      avec_credit: false,
      min_nb_achats: '',
      nouveaux_jours: '',
      search: ''
    });
  };

  // Filtres rapides
  const applyQuickFilter = (type) => {
    resetFilters();
    switch(type) {
      case 'inactifs':
        setFilters(prev => ({ ...prev, inactif_depuis_jours: '30' }));
        break;
      case 'fideles':
        setFilters(prev => ({ ...prev, min_nb_achats: '5' }));
        break;
      case 'gros_clients':
        setFilters(prev => ({ ...prev, min_achats: '100000' }));
        break;
      case 'nouveaux':
        setFilters(prev => ({ ...prev, nouveaux_jours: '30' }));
        break;
      case 'avec_credit':
        setFilters(prev => ({ ...prev, avec_credit: true }));
        break;
      default:
        break;
    }
  };

  // Supprimer campagne
  const deleteCampaign = async (id) => {
    if (!await confirm({ message: 'Supprimer cette campagne ?', type: 'danger', confirmText: 'Supprimer' })) return;
    try {
      await api.delete(`/marketing/campaigns/${id}`);
      loadData();
    } catch (err) {
      logger.error(err);
    }
  };

  if (loading) {
    return <DjamannLoader variant="section" />;
  }

  return (
    <div className="p-4 md:p-6 max-w-7xl mx-auto">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
        <div>
          <h1 className="text-xl md:text-2xl font-bold text-gray-800 flex items-center gap-2">
            <Megaphone className="w-6 h-6" /> Marketing Ciblé
          </h1>
          <p className="text-gray-500 text-sm">Segmentez vos clients et créez des campagnes personnalisées</p>
        </div>
        <Link
          to="/workspace/clients"
          className="text-indigo-600 hover:text-indigo-800 text-sm flex items-center gap-1"
        >
          <ArrowLeft className="w-4 h-4" /> Retour aux clients
        </Link>
      </div>

      {/* Stats rapides */}
      {stats && (
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-6">
          <div className="bg-white rounded-xl shadow p-4 text-center">
            <div className="text-2xl font-bold text-indigo-600">{stats.total_clients}</div>
            <div className="text-xs text-gray-500">Clients total</div>
          </div>
          <div className="bg-white rounded-xl shadow p-4 text-center">
            <div className="text-2xl font-bold text-green-600">{stats.clients_avec_segments}</div>
            <div className="text-xs text-gray-500">Avec segments</div>
          </div>
          <div className="bg-white rounded-xl shadow p-4 text-center">
            <div className="text-2xl font-bold text-purple-600">{stats.nb_segments}</div>
            <div className="text-xs text-gray-500">Segments</div>
          </div>
          <div className="bg-white rounded-xl shadow p-4 text-center">
            <div className="text-2xl font-bold text-amber-600">{stats.campagnes_mois}</div>
            <div className="text-xs text-gray-500">Campagnes ce mois</div>
          </div>
        </div>
      )}

      {/* Tabs */}
      <div className="flex gap-2 mb-6 overflow-x-auto pb-2">
        <button
          onClick={() => setActiveTab('clients')}
          className={`px-4 py-2 rounded-xl font-medium transition whitespace-nowrap ${
            activeTab === 'clients' 
              ? 'bg-indigo-600 text-white' 
              : 'bg-white text-gray-600 hover:bg-gray-50'
          }`}
        >
          <Users className="w-4 h-4 inline mr-1" /> Clients ({clients.length})
        </button>
        <button
          onClick={() => setActiveTab('segments')}
          className={`px-4 py-2 rounded-xl font-medium transition whitespace-nowrap ${
            activeTab === 'segments' 
              ? 'bg-indigo-600 text-white' 
              : 'bg-white text-gray-600 hover:bg-gray-50'
          }`}
        >
          <Tag className="w-4 h-4 inline mr-1" /> Segments ({segments.length})
        </button>
        <button
          onClick={() => setActiveTab('campaigns')}
          className={`px-4 py-2 rounded-xl font-medium transition whitespace-nowrap ${
            activeTab === 'campaigns' 
              ? 'bg-indigo-600 text-white' 
              : 'bg-white text-gray-600 hover:bg-gray-50'
          }`}
        >
          <Target className="w-4 h-4 inline mr-1" /> Campagnes ({campaigns.length})
        </button>
      </div>

      {/* Tab: Clients */}
      {activeTab === 'clients' && (
        <div>
          {/* Barre d'actions */}
          <div className="bg-white rounded-xl shadow p-4 mb-4">
            <div className="flex flex-col sm:flex-row gap-3">
              {/* Recherche */}
              <div className="flex-1">
                <input
                  type="text"
                  placeholder="Rechercher un client..."
                  value={filters.search}
                  onChange={(e) => setFilters(prev => ({ ...prev, search: e.target.value }))}
                  className="w-full px-4 py-2 border rounded-xl focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                />
              </div>
              {/* Boutons */}
              <div className="flex gap-2">
                <button
                  onClick={() => setShowFilters(!showFilters)}
                  className={`px-4 py-2 rounded-xl transition flex items-center gap-2 ${
                    showFilters ? 'bg-indigo-100 text-indigo-700' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                  }`}
                >
                  <Filter className="w-4 h-4 inline mr-1" /> Filtres
                </button>
                {selectedClients.length > 0 && (
                  <button
                    onClick={() => setShowMessageModal(true)}
                    className="px-4 py-2 bg-green-600 text-white rounded-xl hover:bg-green-700 transition flex items-center gap-2"
                  >
                    <Download className="w-4 h-4 inline mr-1" /> Exporter ({selectedClients.length})
                  </button>
                )}
              </div>
            </div>

            {/* Filtres rapides */}
            <div className="flex flex-wrap gap-2 mt-3">
              <span className="text-sm text-gray-500">Filtres rapides:</span>
              <button onClick={() => applyQuickFilter('inactifs')} className="px-3 py-1 text-xs bg-red-100 text-red-700 rounded-full hover:bg-red-200 flex items-center gap-1">
                <Moon className="w-3 h-3" /> Inactifs 30j
              </button>
              <button onClick={() => applyQuickFilter('fideles')} className="px-3 py-1 text-xs bg-green-100 text-green-700 rounded-full hover:bg-green-200 flex items-center gap-1">
                <Heart className="w-3 h-3" /> Fidèles (5+ achats)
              </button>
              <button onClick={() => applyQuickFilter('gros_clients')} className="px-3 py-1 text-xs bg-amber-100 text-amber-700 rounded-full hover:bg-amber-200 flex items-center gap-1">
                <Coins className="w-3 h-3" /> Gros clients (100k+)
              </button>
              <button onClick={() => applyQuickFilter('nouveaux')} className="px-3 py-1 text-xs bg-blue-100 text-blue-700 rounded-full hover:bg-blue-200 flex items-center gap-1">
                <UserPlus className="w-3 h-3" /> Nouveaux (30j)
              </button>
              <button onClick={() => applyQuickFilter('avec_credit')} className="px-3 py-1 text-xs bg-orange-100 text-orange-700 rounded-full hover:bg-orange-200 flex items-center gap-1">
                <CreditCard className="w-3 h-3" /> Avec crédit
              </button>
              <button onClick={resetFilters} className="px-3 py-1 text-xs bg-gray-100 text-gray-600 rounded-full hover:bg-gray-200 flex items-center gap-1">
                <X className="w-3 h-3" /> Réinitialiser
              </button>
            </div>

            {/* Filtres avancés */}
            {showFilters && (
              <div className="mt-4 pt-4 border-t grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3">
                <div>
                  <label className="block text-xs text-gray-500 mb-1">Segment</label>
                  <select
                    value={filters.segment_ids[0] || ''}
                    onChange={(e) => setFilters(prev => ({ 
                      ...prev, 
                      segment_ids: e.target.value ? [e.target.value] : [] 
                    }))}
                    className="w-full px-3 py-2 border rounded-lg text-sm"
                  >
                    <option value="">Tous</option>
                    {segments.map(s => (
                      <option key={s.id} value={s.id}>{s.nom}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-xs text-gray-500 mb-1">Achats min (XOF)</label>
                  <input
                    type="number"
                    value={filters.min_achats}
                    onChange={(e) => setFilters(prev => ({ ...prev, min_achats: e.target.value }))}
                    className="w-full px-3 py-2 border rounded-lg text-sm"
                    placeholder="0"
                  />
                </div>
                <div>
                  <label className="block text-xs text-gray-500 mb-1">Achats max (XOF)</label>
                  <input
                    type="number"
                    value={filters.max_achats}
                    onChange={(e) => setFilters(prev => ({ ...prev, max_achats: e.target.value }))}
                    className="w-full px-3 py-2 border rounded-lg text-sm"
                    placeholder="Illimite"
                  />
                </div>
                <div>
                  <label className="block text-xs text-gray-500 mb-1">Inactif depuis (jours)</label>
                  <input
                    type="number"
                    value={filters.inactif_depuis_jours}
                    onChange={(e) => setFilters(prev => ({ ...prev, inactif_depuis_jours: e.target.value }))}
                    className="w-full px-3 py-2 border rounded-lg text-sm"
                    placeholder="30"
                  />
                </div>
                <div>
                  <label className="block text-xs text-gray-500 mb-1">Actif depuis (jours)</label>
                  <input
                    type="number"
                    value={filters.actif_depuis_jours}
                    onChange={(e) => setFilters(prev => ({ ...prev, actif_depuis_jours: e.target.value }))}
                    className="w-full px-3 py-2 border rounded-lg text-sm"
                    placeholder="7"
                  />
                </div>
                <div>
                  <label className="block text-xs text-gray-500 mb-1">Nb achats min</label>
                  <input
                    type="number"
                    value={filters.min_nb_achats}
                    onChange={(e) => setFilters(prev => ({ ...prev, min_nb_achats: e.target.value }))}
                    className="w-full px-3 py-2 border rounded-lg text-sm"
                    placeholder="1"
                  />
                </div>
                <div>
                  <label className="block text-xs text-gray-500 mb-1">Nouveaux (jours)</label>
                  <input
                    type="number"
                    value={filters.nouveaux_jours}
                    onChange={(e) => setFilters(prev => ({ ...prev, nouveaux_jours: e.target.value }))}
                    className="w-full px-3 py-2 border rounded-lg text-sm"
                    placeholder="30"
                  />
                </div>
                <div className="flex items-end">
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={filters.avec_credit}
                      onChange={(e) => setFilters(prev => ({ ...prev, avec_credit: e.target.checked }))}
                      className="w-4 h-4 rounded text-indigo-600"
                    />
                    <span className="text-sm">Avec crédit</span>
                  </label>
                </div>
              </div>
            )}
          </div>

          {/* Ajouter au segment */}
          {selectedClients.length > 0 && segments.length > 0 && (
            <div className="bg-indigo-50 rounded-xl p-3 mb-4 flex flex-wrap items-center gap-2">
              <span className="text-sm text-indigo-700">Ajouter {selectedClients.length} client(s) au segment:</span>
              {segments.map(s => (
                <button
                  key={s.id}
                  onClick={() => addToSegment(s.id)}
                  className="px-3 py-1 text-xs rounded-full hover:opacity-80 transition"
                  style={{ backgroundColor: s.couleur + '20', color: s.couleur }}
                >
                  <span className="inline-flex items-center gap-1">{getSegmentIcon(s.icone, 'w-3 h-3')} {s.nom}</span>
                </button>
              ))}
            </div>
          )}

          {/* Pagination info */}
          {(() => {
            const totalPages = Math.ceil(clients.length / itemsPerPage);
            const startIndex = (currentPage - 1) * itemsPerPage;
            const paginatedClients = clients.slice(startIndex, startIndex + itemsPerPage);
            
            return (
              <>
                {/* Liste des clients */}
                <div className="bg-white rounded-xl shadow overflow-hidden">
                  <table className="w-full">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-4 py-3 text-left">
                          <input
                            type="checkbox"
                            checked={selectedClients.length === clients.length && clients.length > 0}
                            onChange={toggleSelectAll}
                            className="w-4 h-4 rounded text-indigo-600"
                          />
                        </th>
                        <th className="px-4 py-3 text-left text-sm font-medium text-gray-600">Client</th>
                        <th className="px-4 py-3 text-left text-sm font-medium text-gray-600 hidden md:table-cell">Contact</th>
                        <th className="px-4 py-3 text-right text-sm font-medium text-gray-600 hidden sm:table-cell">Total achats</th>
                        <th className="px-4 py-3 text-right text-sm font-medium text-gray-600 hidden sm:table-cell">Reste à payer</th>
                        <th className="px-4 py-3 text-center text-sm font-medium text-gray-600 hidden lg:table-cell">Dernier achat</th>
                        <th className="px-4 py-3 text-left text-sm font-medium text-gray-600">Segments</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y">
                      {paginatedClients.length === 0 ? (
                        <tr>
                          <td colSpan="7" className="px-4 py-8 text-center text-gray-500">
                            Aucun client ne correspond aux filtres
                          </td>
                        </tr>
                      ) : (
                        paginatedClients.map(client => (
                          <tr key={client.id} className={`hover:bg-gray-50 ${selectedClients.includes(client.id) ? 'bg-indigo-50' : ''}`}>
                            <td className="px-4 py-3">
                              <input
                                type="checkbox"
                                checked={selectedClients.includes(client.id)}
                                onChange={() => toggleSelectClient(client.id)}
                                className="w-4 h-4 rounded text-indigo-600"
                              />
                            </td>
                            <td className="px-4 py-3">
                              <div className="font-medium text-gray-800">{client.nom}</div>
                              <div className="text-xs text-gray-500 md:hidden">{client.telephone}</div>
                            </td>
                            <td className="px-4 py-3 hidden md:table-cell">
                              <div className="text-sm text-gray-600">{client.telephone}</div>
                              <div className="text-xs text-gray-400">{client.email}</div>
                            </td>
                            <td className="px-4 py-3 text-right hidden sm:table-cell">
                              <div className="font-medium text-gray-800">
                                {Number(client.total_achats || 0).toLocaleString()} XOF
                              </div>
                              <div className="text-xs text-gray-500">{client.nb_achats || 0} achats</div>
                            </td>
                            <td className="px-4 py-3 text-right hidden sm:table-cell">
                              {Number(client.reste_total || 0) > 0 ? (
                                <div>
                                  <div className="font-medium text-red-600">
                                    {Number(client.reste_total || 0).toLocaleString()} XOF
                                  </div>
                                  <div className="text-xs text-gray-500">
                                    {Number(client.reste_factures || 0) > 0 && (
                                      <span className="text-orange-500">{Number(client.nb_factures_impayees || 0)} fact.</span>
                                    )}
                                    {Number(client.reste_factures || 0) > 0 && Number(client.reste_prestations || 0) > 0 && ' + '}
                                    {Number(client.reste_prestations || 0) > 0 && (
                                      <span className="text-purple-500">{Number(client.nb_prestations_impayees || 0)} prest.</span>
                                    )}
                                  </div>
                                </div>
                              ) : (
                                <span className="text-xs text-green-500 flex items-center gap-1"><Check className="w-3 h-3" /> À jour</span>
                              )}
                            </td>
                            <td className="px-4 py-3 text-center hidden lg:table-cell">
                              <span className={`text-sm ${client.dernier_achat ? 'text-gray-600' : 'text-gray-400'}`}>
                                {client.dernier_achat 
                                  ? new Date(client.dernier_achat).toLocaleDateString('fr-FR')
                                  : 'Jamais'
                                }
                              </span>
                            </td>
                            <td className="px-4 py-3">
                              <div className="flex flex-wrap gap-1">
                                {client.segments_noms ? (
                                  client.segments_noms.split(', ').map((seg, i) => (
                                    <span key={i} className="px-2 py-0.5 text-xs bg-indigo-100 text-indigo-700 rounded-full">
                                      {seg}
                                    </span>
                                  ))
                                ) : (
                                  <span className="text-xs text-gray-400">-</span>
                                )}
                              </div>
                            </td>
                          </tr>
                        ))
                      )}
                    </tbody>
                  </table>
                </div>

                {/* Pagination */}
                {totalPages > 1 && (
                  <div className="flex flex-col sm:flex-row justify-between items-center gap-4 mt-4">
                    <div className="text-sm text-gray-500">
                      Affichage {startIndex + 1} - {Math.min(startIndex + itemsPerPage, clients.length)} sur {clients.length} clients
                    </div>
                    <div className="flex items-center gap-2">
                      <button
                        onClick={() => setCurrentPage(1)}
                        disabled={currentPage === 1}
                        className="px-3 py-1 rounded-lg border disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50"
                      >
                        <ChevronsLeft className="w-4 h-4" />
                      </button>
                      <button
                        onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
                        disabled={currentPage === 1}
                        className="px-3 py-1 rounded-lg border disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50"
                      >
                        <ChevronLeft className="w-4 h-4" />
                      </button>
                      <span className="px-4 py-1 bg-indigo-100 text-indigo-700 rounded-lg font-medium">
                        {currentPage} / {totalPages}
                      </span>
                      <button
                        onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
                        disabled={currentPage === totalPages}
                        className="px-3 py-1 rounded-lg border disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50"
                      >
                        <ChevronRight className="w-4 h-4" />
                      </button>
                      <button
                        onClick={() => setCurrentPage(totalPages)}
                        disabled={currentPage === totalPages}
                        className="px-3 py-1 rounded-lg border disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50"
                      >
                        <ChevronsRight className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                )}
              </>
            );
          })()}
        </div>
      )}

      {/* Tab: Segments */}
      {activeTab === 'segments' && (
        <div>
          <div className="flex justify-between items-center mb-4">
            <p className="text-gray-500 text-sm">Créez des segments pour organiser vos clients</p>
            <button
              onClick={() => openSegmentModal()}
              className="px-4 py-2 bg-indigo-600 text-white rounded-xl hover:bg-indigo-700 transition flex items-center gap-2"
            >
              <Plus className="w-4 h-4 inline mr-1" /> Nouveau segment
            </button>
          </div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {segments.length === 0 ? (
              <div className="sm:col-span-2 lg:col-span-3 bg-white rounded-xl shadow p-8 text-center">
                <Tag className="w-12 h-12 mx-auto mb-3 text-gray-400" />
                <h3 className="font-medium text-gray-800 mb-2">Aucun segment</h3>
                <p className="text-gray-500 text-sm mb-4">Créez des segments pour grouper vos clients</p>
                <button
                  onClick={() => openSegmentModal()}
                  className="px-4 py-2 bg-indigo-600 text-white rounded-xl hover:bg-indigo-700 transition"
                >
                  Créer un segment
                </button>
              </div>
            ) : (
              segments.map(segment => (
                <div key={segment.id} className="bg-white rounded-xl shadow p-4 hover:shadow-md transition">
                  <div className="flex items-start justify-between mb-3">
                    <div className="flex items-center gap-3">
                      <div 
                        className="w-12 h-12 rounded-xl flex items-center justify-center text-2xl"
                        style={{ backgroundColor: segment.couleur + '20' }}
                      >
                        {getSegmentIcon(segment.icone, 'w-6 h-6')}
                      </div>
                      <div>
                        <h3 className="font-medium text-gray-800">{segment.nom}</h3>
                        <p className="text-sm text-gray-500">{segment.nb_clients || 0} clients</p>
                      </div>
                    </div>
                    <div className="flex gap-1">
                      <button
                        onClick={() => openSegmentModal(segment)}
                        className="p-2 text-gray-400 hover:text-indigo-600 hover:bg-indigo-50 rounded-lg transition"
                      >
                        <Pencil className="w-4 h-4" />
                      </button>
                      <button
                        onClick={() => deleteSegment(segment.id)}
                        className="p-2 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                  {segment.description && (
                    <p className="text-sm text-gray-500">{segment.description}</p>
                  )}
                  <button
                    onClick={() => {
                      setFilters(prev => ({ ...prev, segment_ids: [segment.id.toString()] }));
                      setActiveTab('clients');
                    }}
                    className="mt-3 w-full py-2 text-sm text-indigo-600 bg-indigo-50 rounded-lg hover:bg-indigo-100 transition"
                  >
                    <span className="flex items-center justify-center gap-1">Voir les clients <ArrowRight className="w-4 h-4" /></span>
                  </button>
                </div>
              ))
            )}
          </div>
        </div>
      )}

      {/* Tab: Campagnes */}
      {activeTab === 'campaigns' && (
        <div>
          <p className="text-gray-500 text-sm mb-4">Historique de vos campagnes marketing</p>

          <div className="bg-white rounded-xl shadow overflow-hidden">
            <table className="w-full">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-3 text-left text-sm font-medium text-gray-600">Campagne</th>
                  <th className="px-4 py-3 text-center text-sm font-medium text-gray-600 hidden sm:table-cell">Type</th>
                  <th className="px-4 py-3 text-center text-sm font-medium text-gray-600">Clients</th>
                  <th className="px-4 py-3 text-center text-sm font-medium text-gray-600 hidden md:table-cell">Statut</th>
                  <th className="px-4 py-3 text-center text-sm font-medium text-gray-600">Date</th>
                  <th className="px-4 py-3 text-center text-sm font-medium text-gray-600">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y">
                {campaigns.length === 0 ? (
                  <tr>
                    <td colSpan="6" className="px-4 py-8 text-center text-gray-500">
                      Aucune campagne créée
                    </td>
                  </tr>
                ) : (
                  campaigns.map(campaign => (
                    <tr key={campaign.id} className="hover:bg-gray-50">
                      <td className="px-4 py-3">
                        <div className="font-medium text-gray-800">{campaign.nom}</div>
                        <div className="text-xs text-gray-500 truncate max-w-xs">
                          {campaign.message_template?.substring(0, 50)}...
                        </div>
                      </td>
                      <td className="px-4 py-3 text-center hidden sm:table-cell">
                        <span className="px-2 py-1 text-xs rounded-full bg-gray-100 text-gray-600">
                          {campaign.type}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-center">
                        <span className="font-medium">{campaign.nb_clients}</span>
                      </td>
                      <td className="px-4 py-3 text-center hidden md:table-cell">
                        <span className={`px-2 py-1 text-xs rounded-full ${
                          campaign.statut === 'exportee' 
                            ? 'bg-green-100 text-green-700' 
                            : 'bg-gray-100 text-gray-600'
                        }`}>
                          {campaign.statut}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-center text-sm text-gray-600">
                        {new Date(campaign.created_at).toLocaleDateString('fr-FR')}
                      </td>
                      <td className="px-4 py-3 text-center">
                        <button
                          onClick={() => deleteCampaign(campaign.id)}
                          className="p-2 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Modal: Segment */}
      {showSegmentModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl w-full max-w-md">
            <div className="p-4 border-b flex justify-between items-center">
              <h2 className="font-bold text-lg">
                {editingSegment ? 'Modifier le segment' : 'Nouveau segment'}
              </h2>
              <button onClick={() => setShowSegmentModal(false)} className="text-gray-400 hover:text-gray-600"><X className="w-5 h-5" /></button>
            </div>
            <form onSubmit={handleSegmentSubmit} className="p-4 space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Nom *</label>
                <input
                  type="text"
                  value={segmentForm.nom}
                  onChange={(e) => setSegmentForm(prev => ({ ...prev, nom: e.target.value }))}
                  className="w-full px-4 py-2 border rounded-xl focus:ring-2 focus:ring-indigo-500"
                  placeholder="ex: VIP, Fidèle, Nouveau..."
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
                <textarea
                  value={segmentForm.description}
                  onChange={(e) => setSegmentForm(prev => ({ ...prev, description: e.target.value }))}
                  className="w-full px-4 py-2 border rounded-xl focus:ring-2 focus:ring-indigo-500"
                  rows="2"
                  placeholder="Description optionnelle..."
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Icône</label>
                <div className="flex flex-wrap gap-2">
                  {SEGMENT_ICON_IDS.map(iconId => (
                    <button
                      key={iconId}
                      type="button"
                      onClick={() => setSegmentForm(prev => ({ ...prev, icone: iconId }))}
                      className={`w-10 h-10 flex items-center justify-center rounded-lg border-2 transition ${
                        segmentForm.icone === iconId 
                          ? 'border-indigo-500 bg-indigo-50 text-indigo-600' 
                          : 'border-gray-200 hover:border-gray-300 text-gray-600'
                      }`}
                    >
                      {getSegmentIcon(iconId, 'w-5 h-5')}
                    </button>
                  ))}
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Couleur</label>
                <div className="flex flex-wrap gap-2">
                  {SEGMENT_COLORS.map(color => (
                    <button
                      key={color.value}
                      type="button"
                      onClick={() => setSegmentForm(prev => ({ ...prev, couleur: color.value }))}
                      className={`w-8 h-8 rounded-full transition ring-2 ring-offset-2 ${
                        segmentForm.couleur === color.value 
                          ? 'ring-gray-400' 
                          : 'ring-transparent hover:ring-gray-200'
                      }`}
                      style={{ backgroundColor: color.value }}
                      title={color.label}
                    />
                  ))}
                </div>
              </div>
              <div className="flex gap-3 pt-2">
                <button
                  type="button"
                  onClick={() => setShowSegmentModal(false)}
                  className="flex-1 py-2 border rounded-xl text-gray-600 hover:bg-gray-50"
                >
                  Annuler
                </button>
                <button
                  type="submit"
                  className="flex-1 py-2 bg-indigo-600 text-white rounded-xl hover:bg-indigo-700"
                >
                  {editingSegment ? 'Modifier' : 'Créer'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Modal: Message & Export */}
      {showMessageModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4 overflow-y-auto">
          <div className="bg-white rounded-2xl w-full max-w-2xl my-4">
            <div className="p-4 border-b flex justify-between items-center">
              <h2 className="font-bold text-lg flex items-center gap-1"><Download className="w-5 h-5" /> Exporter {selectedClients.length} clients</h2>
              <button onClick={() => setShowMessageModal(false)} className="text-gray-400 hover:text-gray-600"><X className="w-5 h-5" /></button>
            </div>
            <div className="p-4 space-y-4 max-h-[70vh] overflow-y-auto">
              {/* Nom campagne */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Nom de la campagne</label>
                <input
                  type="text"
                  value={campaignName}
                  onChange={(e) => setCampaignName(e.target.value)}
                  className="w-full px-4 py-2 border rounded-xl focus:ring-2 focus:ring-indigo-500"
                  placeholder={`Campagne du ${new Date().toLocaleDateString('fr-FR')}`}
                />
              </div>

              {/* Type campagne */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Type</label>
                <select
                  value={campaignType}
                  onChange={(e) => setCampaignType(e.target.value)}
                  className="w-full px-4 py-2 border rounded-xl focus:ring-2 focus:ring-indigo-500"
                >
                  <option value="personnalise">Personnalisé</option>
                  <option value="promotion">Promotion / Offre</option>
                  <option value="relance">Relance clients</option>
                  <option value="fidelite">Fidélité</option>
                  <option value="newsletter">Newsletter</option>
                </select>
              </div>

              {/* Variables disponibles */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Variables disponibles</label>
                <div className="flex flex-wrap gap-2">
                  {AVAILABLE_VARIABLES.map(v => (
                    <button
                      key={v.var}
                      type="button"
                      onClick={() => insertVariable(v.var)}
                      className="px-2 py-1 text-xs bg-indigo-100 text-indigo-700 rounded hover:bg-indigo-200 transition"
                      title={`${v.label} (ex: ${v.example})`}
                    >
                      {v.var}
                    </button>
                  ))}
                </div>
              </div>

              {/* Message template */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Message personnalisé</label>
                <textarea
                  value={messageTemplate}
                  onChange={(e) => setMessageTemplate(e.target.value)}
                  className="w-full px-4 py-2 border rounded-xl focus:ring-2 focus:ring-indigo-500 font-mono text-sm"
                  rows="6"
                  placeholder="Votre message avec {variables}..."
                />
              </div>

              {/* Aperçu */}
              <div>
                <div className="flex justify-between items-center mb-1">
                  <label className="block text-sm font-medium text-gray-700">Aperçu</label>
                  {clients.length > 0 && (
                    <button
                      type="button"
                      onClick={() => handlePreview(clients[0].id)}
                      className="text-xs text-indigo-600 hover:underline"
                    >
                      Tester avec {clients[0].nom}
                    </button>
                  )}
                </div>
                <div className="bg-gray-50 rounded-xl p-3 text-sm text-gray-600 whitespace-pre-wrap">
                  {previewMessage || 'Cliquez sur "Tester" pour voir l\'aperçu'}
                </div>
              </div>

              {/* Option sauvegarder */}
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={saveCampaign}
                  onChange={(e) => setSaveCampaign(e.target.checked)}
                  className="w-4 h-4 rounded text-indigo-600"
                />
                <span className="text-sm text-gray-600">Sauvegarder dans l'historique des campagnes</span>
              </label>
            </div>
            <div className="p-4 border-t flex gap-3">
              <button
                type="button"
                onClick={() => setShowMessageModal(false)}
                className="flex-1 py-2 border rounded-xl text-gray-600 hover:bg-gray-50"
              >
                Annuler
              </button>
              <button
                type="button"
                onClick={handleExport}
                disabled={exporting}
                className="flex-1 py-2 bg-green-600 text-white rounded-xl hover:bg-green-700 disabled:opacity-50 flex items-center justify-center gap-2"
              >
                {exporting ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent"></div>
                    Export...
                  </>
                ) : (
                  <><Download className="w-4 h-4 inline mr-1" /> Télécharger Excel</>
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default BusinessMarketing;
